import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { DenikVysledekHledani } from '../../api/types.js';
import { Chyba, Input, Nacitani } from '../../components/ui.js';
import { formatDen } from '../../lib/format.js';

/**
 * Fulltext v deníku (D4): pole přímo na obrazovce deníku (spuštění ≤2 ťuknutí),
 * výsledky s datem a zvýrazněním z ts_headline. API vrací `<mark>…</mark>` —
 * zvýraznění se vykresluje BEZ dangerouslySetInnerHTML (jen tag mark se
 * převádí na element, zbytek je čistý text).
 */

export function zvyrazni(nahled: string): ReactNode[] {
  return nahled.split('<mark>').flatMap((cast, i) => {
    if (i === 0) return [cast];
    const [zvyraznene, ...zbytek] = cast.split('</mark>');
    return [<mark key={i}>{zvyraznene}</mark>, zbytek.join('</mark>')];
  });
}

export function HledaniDeniku({
  zakazkaId,
  cestaZaznamu = (id) => `/teren/denik/${id}`,
}: {
  zakazkaId?: string;
  /** Kam vede proklik výsledku (terén vs. kancelář). */
  cestaZaznamu?: (zaznamId: string) => string;
}) {
  const [text, setText] = useState('');
  const [dotaz, setDotaz] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['denik-hledani', zakazkaId ?? 'vse', dotaz],
    queryFn: () => {
      const params = new URLSearchParams({ q: dotaz });
      if (zakazkaId) params.set('zakazkaId', zakazkaId);
      return api.get<DenikVysledekHledani[]>(`/api/diary/search?${params.toString()}`);
    },
    enabled: dotaz.length >= 2,
  });

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-2 text-lg font-bold">Hledat v deníku</h2>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setDotaz(text.trim());
        }}
      >
        <Input
          type="search"
          aria-label="Hledat v deníku"
          placeholder="např. betonáž stropu…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button
          type="submit"
          className="min-h-12 shrink-0 rounded-lg bg-primary px-4 text-base font-semibold text-white"
        >
          Hledat
        </button>
      </form>
      {dotaz.length >= 2 && (
        <div className="mt-3">
          {isLoading && <Nacitani text="Hledám…" />}
          {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
          {data && data.length === 0 && (
            <p className="text-base text-text-secondary">Nic nenalezeno — zkuste jiné slovo.</p>
          )}
          <ul className="flex flex-col">
            {(data ?? []).map((v, i) => (
              <li key={`${v.zaznamId}-${v.zdroj}-${i}`}>
                <Link
                  to={cestaZaznamu(v.zaznamId)}
                  className="block min-h-12 border-t border-border py-2 first:border-t-0"
                >
                  <span className="font-bold">{formatDen(v.datum)}</span>
                  {v.zdroj === 'priloha' ? (
                    <span className="text-sm text-text-secondary"> (popisek fotky)</span>
                  ) : null}
                  <span className="block text-base">{zvyrazni(v.nahled)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
